import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import AnimatedBorderPill from './AnimatedBorderPill';

interface FAQItem {
  question: string;
  answer: string;
}

const EditorialFAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { theme } = useTheme();

  // Theme-aware colors
  const headingColor = theme === 'dark' ? '#FFFFFF' : '#111111';
  const textColor = theme === 'dark' ? '#AAAAAA' : '#555555';
  const borderColor = theme === 'dark' ? '#333333' : '#E0E0E0';
  const cardBgColor = theme === 'dark' ? '#1A1A1A' : '#FFFFFF';
  const badgeBgColor = theme === 'dark' ? '#1A1A1A' : '#FFFFFF';
  const badgeBorderGradient = theme === 'dark' 
    ? 'linear-gradient(90deg, transparent 0%, transparent 40%, rgba(255,255,255,0.6) 50%, transparent 60%, transparent 100%)'
    : 'linear-gradient(90deg, transparent 0%, transparent 40%, rgba(0,0,0,0.6) 50%, transparent 60%, transparent 100%)';
  const badgeBorderColor = theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)';

  const faqs: FAQItem[] = [
    {
      question: 'Am I tied into a long-term commitment or contract?',
      answer:
        'Absolutely not. All of our plans are offered on a simple month-to-month basis. You can upgrade, downgrade, or cancel anytime with zero penalties or hidden termination fees. Your success keeps you here, not a contract.'
    },
    {
      question: 'How is your solution different from a generic tool?',
      answer:
        'Storily is a fully white-labeled platform powered by industry-leading software. Storily provides custom workflows, templates, and support tailored specifically for business owners and agencies, ensuring you have a proven system, not just a set of generic features.'
    },
    {
      question: 'Are there restrictions on how many team members can use the platform?',
      answer:
        'No. Storily encourages team collaboration! Your subscription includes unlimited user seats, meaning you can add your entire sales, marketing, and support staff without ever incurring an extra per-user fee.'
    },
    {
      question: 'How much money will I save by consolidating my tools?',
      answer:
        'Our clients typically replace 5-10 separate subscriptions, including ClickFunnels, Mailchimp, Calendly, and CallRail. The savings usually amount to hundreds of dollars per month, not including the immense time saved from managing separate logins.'
    },
    {
      question: 'Who owns my client data and content?',
      answer:
        'You do. All your customer data, sales pipelines, and content remain 100% your property and are stored securely. Storily simply provides the branded platform for you to manage it.'
    },
    {
      question: 'Do I need to be tech-savvy to use this platform?',
      answer:
        'Not at all. The system uses a simple drag-and-drop interface for all tasks. Plus, your subscription includes full onboarding and a private video library to guide you every step of the way.'
    },
    {
      question: 'Can I connect my existing domain and email?',
      answer:
        'Yes. Storily helps you easily connect your existing domain for your websites and funnels. Storily also configures your business email and phone number to ensure consistent, professional branding on all client communication.'
    },
    {
      question: 'Does the AI cost extra or is it included in the price?',
      answer:
        'All of our core AI tools are included. There is a small usage fee for high-volume actions like AI Voice calls and mass texting/emails, which is passed through transparently, so you only pay for what you use.'
    },
    {
      question: 'Is this platform suitable for managing multiple business locations?',
      answer:
        'Yes. The platform is designed for scale. You can create separate, customized accounts for unlimited locations or client businesses all managed from your single agency dashboard.'
    },
    {
      question: 'How do I get technical support if I have an issue?',
      answer:
        'You receive dedicated support directly from the Storily team. Storily offers responsive live chat and email support available during business hours, ensuring you get fast, expert help.'
    }
  ];

  return (
    <section className="pt-16 pb-20 lg:pt-16 lg:pb-32 transition-colors duration-300">
      <div className="max-w-[960px] mx-auto px-[7%] sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            {/* Answers Badge - uses stable global CSS classes */}
            <div className="flex justify-center mb-4">
              <AnimatedBorderPill text="Answers" icon={HelpCircle} />
            </div>
            
            <h2 className="text-3xl lg:text-4xl font-bold" style={{ color: headingColor }}>
              Frequently Asked Questions.
            </h2>
            <p className="mt-2 text-sm" style={{ color: textColor }}>
              Everything you need to know about our platform.
            </p>
          </div>
          
          
          {/* FAQ Items - v2 styling with v3 bento card look */}
          <div className="space-y-2">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="overflow-hidden transition-all"
                style={{ 
                  backgroundColor: cardBgColor,
                  border: `1px solid ${borderColor}`, 
                  borderRadius: '12px',
                }}
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-6 py-3 sm:px-8 sm:py-4 flex items-center justify-between text-left"
                >
                  <h3 className="text-xs sm:text-sm font-semibold pr-4" style={{ color: headingColor }}>
                    {faq.question}
                  </h3>
                  <ChevronDown 
                    className={`w-4 h-4 sm:w-5 sm:h-5 shrink-0 transition-transform duration-300 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                    style={{ color: headingColor }}
                  />
                </button>
                
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-6 pb-3 sm:px-8 sm:pb-4">
                    <p className="text-xs sm:text-sm leading-relaxed" style={{ color: textColor }}>
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default EditorialFAQ;
